import { formatNumber } from "helpers/utils";
import type { TopPerformingData } from "store/analyticsStore";

export type ChannelConsolidatedSales = {
    avatarUrl: string;
    channelId: number;
    channelKind: number;
    channelName: string;
    companyId: number;
    countryMarket: string;
    cumulatedQuantities: number;
    cumulatedQuantitiesReturned: number;
    cumulatedTurnover: number;
    cumulatedTurnoverPostDistributor: number;
    date: string;
    distributorPercentage?: number;
    ean13: string;
    is_in_range: number;
    name: string;
    optimalPrice: number;
    optimalPriceCurrency: string;
    periodId: number;
    quantities: number;
    quantitiesReturned: number;
    saleId: number;
    saleMonth: number;
    saleYear: number;
    titleId: number;
    totalCumulatedQuantities: number;
    totalCumulatedQuantitiesReturned: number;
    totalCumulatedTurnover: number;
    totalCumulatedTurnoverPostDistributor: number;
    totalQuantities: number;
    totalQuantitiesReturned: number;
    totalTurnover: number;
    totalTurnoverPostDistributor: number;
    turnover: number;
    turnoverPostDistributor: number;
};

export type AnalyticsCumulatedSalesItem = {
    id: number;
    avatarUrl?: string;
    channelId?: number;
    channelKind?: number;
    channelName?: string;
    companyId?: number;
    countryMarket?: string;
    cumulatedQty?: number;
    cumulatedReturnedQty?: number;
    cumulatedRevenue?: number;
    cumulatedRevenuePostDistributor?: number;
    cumulatedTurnover1?: number;
    cumulatedTurnover2?: number;
    date?: string;
    distributorPercentage?: number;
    ean13?: string;
    name: string;
    optimalPrice?: number;
    optimalPriceCurrency?: string;
    periodId?: number;
    quantities?: number;
    quantitiesReturned?: number;
    saleId: number;
    saleMonth?: number;
    saleYear?: number;
    titleId: number;
    turnover?: number;
    turnoverPostDistributor?: number;
    turnover_1?: number | null;
    turnover_2?: number | null;
};

const ChannelConsolidatedSalesTableColumns = ["title", "qty-returned", "qty-net", "ca-ppht", "rev"] as const;

export type ChannelConsolidatedSalesTableRow = {
    [K in (typeof ChannelConsolidatedSalesTableColumns)[number]]: string;
} & {
    id: string;
};

export type ChannelConsolidatedSalesStoreItem = {
    inRange: ChannelConsolidatedSalesTableRow;
    cumulated: ChannelConsolidatedSalesTableRow;
};

type PropsMap = Record<
    string,
    {
        [K in keyof ChannelConsolidatedSalesTableRow]: keyof ChannelConsolidatedSales;
    }
>;

const propsMap: PropsMap = {
    inRange: {
        id: "titleId",
        title: "name",
        "qty-returned": "quantitiesReturned",
        "qty-net": "quantities",
        "ca-ppht": "turnover",
        rev: "turnoverPostDistributor",
    },
    cumulated: {
        id: "titleId",
        title: "name",
        "qty-returned": "cumulatedQuantitiesReturned",
        "qty-net": "cumulatedQuantities",
        "ca-ppht": "cumulatedTurnover",
        rev: "cumulatedTurnoverPostDistributor",
    },
    inRangeTotal: {
        id: "titleId",
        title: "name",
        "qty-returned": "totalQuantitiesReturned",
        "qty-net": "totalQuantities",
        "ca-ppht": "totalTurnover",
        rev: "totalTurnoverPostDistributor",
    },
    cumulatedTotal: {
        id: "titleId",
        title: "name",
        "qty-returned": "totalCumulatedQuantitiesReturned",
        "qty-net": "totalCumulatedQuantities",
        "ca-ppht": "totalCumulatedTurnover",
        rev: "totalCumulatedTurnoverPostDistributor",
    },
};

const currencyValues: Array<keyof ChannelConsolidatedSalesTableRow> = ["rev", "ca-ppht"];

function parseConsolidatedSalesTable(
    rawData: ChannelConsolidatedSales,
    mappingName: keyof typeof propsMap,
): ChannelConsolidatedSalesTableRow {
    const columnsData = ChannelConsolidatedSalesTableColumns.reduce((acc, columnName) => {
        const mappedName = propsMap[mappingName][columnName];

        const value = rawData[mappedName];
        const isCurrency = currencyValues.includes(columnName);
        acc[columnName] =
            columnName === "title"
                ? `${value}`
                : value && typeof value === "number"
                  ? formatNumber(isCurrency ? value : Math.round(value), {
                        isCurrency,
                        always2Digit: isCurrency,
                    })
                  : "";
        return acc;
    }, {} as ChannelConsolidatedSalesTableRow);

    return {
        ...columnsData,
        id: `${rawData.titleId}`,
    };
}

export function parseConsolidatedSales(rawData: ChannelConsolidatedSales): ChannelConsolidatedSalesStoreItem {
    return {
        inRange: parseConsolidatedSalesTable(rawData, "inRange"),
        cumulated: parseConsolidatedSalesTable(rawData, "cumulated"),
    };
}

export function parseConsolidatedSalesTotal(rawData: ChannelConsolidatedSales): ChannelConsolidatedSalesStoreItem {
    return {
        inRange: parseConsolidatedSalesTable(rawData, "inRangeTotal"),
        cumulated: parseConsolidatedSalesTable(rawData, "cumulatedTotal"),
    };
}

export function parseTopPerformingTitle(rawData: AnalyticsCumulatedSalesItem): TopPerformingData {
    return {
        id: rawData.titleId,
        titleId: rawData.titleId,
        name: rawData.name,
        amount: rawData.turnover || 0,
        avatarUrl: rawData.avatarUrl,
    };
}
